import moment from "moment"

import { getFinalDateLabel } from "Utils/widgetsUtils"

/**
 * Traduction d'une PÉRIODE cliquée en intervalle de dates, pour le drill-down.
 *
 * Pourquoi ce module existe : sur un axe temporel, la catégorie affichée par le graphique est un
 * LIBELLÉ de période (« 02/2026 », « T1 2026 »), pas une date. Le drill-down construit d'ordinaire
 * un filtre `colonne == catégorie` ; sur une date il ne matcherait jamais rien et ouvrirait une
 * liste vide — sans erreur, sans indice.
 *
 * ⚠️ Les bornes ne sont **jamais** déduites du libellé. Il est localisé et son format dépend de la
 * fréquence : le relire reviendrait à écrire un formateur à l'envers, qui se décalerait dès qu'on
 * touche à l'autre. On repart de la valeur BRUTE de la ligne, qui est déjà le début du
 * compartiment : le back regroupe par `DATE_TRUNC(unité, colonne)` (`sql-builder.service.ts`), donc
 * une ligne mensuelle porte le 1er du mois, une ligne trimestrielle le 1er jour du trimestre.
 *
 * `specificWidgetData` conserve bien cette valeur brute : c'est `getTransformedData` qui la
 * remplace par le libellé, et il travaille sur un `cloneDeep`.
 */

/**
 * Unité du compartiment, par fréquence de regroupement.
 *
 * ⚠️ Seules figurent ici les fréquences dont le libellé porte l'ANNÉE. Les autres — « Janvier »,
 * « T1 », « Jour 05 », « 05/01 » — agrègent volontairement plusieurs années sous une même
 * catégorie : il n'existe alors aucun intervalle continu à produire, et prétendre le contraire
 * filtrerait sur une seule année choisie au hasard parmi celles empilées dans la barre.
 *
 * `undefined` / `"default"` = aucun regroupement temporel demandé : le libellé est la date du jour
 * (`DD/MM/YYYY`), donc le compartiment est le jour.
 */
const UNITE_PAR_FREQUENCE = {
  year: "year",
  quarterYear: "quarter",
  monthYear: "month",
  monthLetterYear: "month",
  dayMonthYear: "day",
  default: "day",
}

/** Même lecture que `getFinalDateLabel`, pour que les deux ne puissent pas diverger. */
const parseValeurBrute = valeur =>
  typeof valeur === "number" ? moment(valeur) : moment(valeur, ["DD-MM-YYYY"])

/**
 * Les deux filtres feuilles bornant la période cliquée, ou `null` si la question ne se pose pas
 * (champ non temporel, fréquence sans année, ligne introuvable).
 *
 * Rendre `null` plutôt que de deviner est délibéré : l'appelant retombe alors sur son filtre
 * d'égalité habituel, qui reste le bon comportement pour une catégorie non temporelle.
 *
 * @param groupByField  l'entrée `groupBy` du widget (porte `frequency`, `uuid`, `valueId`)
 * @param fieldUUID     la colonne à filtrer
 * @param categoryValue la catégorie cliquée, telle que l'affiche le graphique
 * @param rows          les lignes BRUTES du widget (`specificWidgetData.data`)
 * @param fieldsData    le schéma des champs, pour confirmer qu'on a bien affaire à une date
 * @param trad          les libellés de date (`{ DAY, MONTH, YEAR }`)
 * @param languageId    la langue courante
 */
export function getPeriodRangeFilters({
  groupByField,
  fieldUUID,
  categoryValue,
  rows,
  fieldsData,
  trad,
  languageId,
}) {
  const frequence = groupByField?.frequency ?? "default"
  const unite = UNITE_PAR_FREQUENCE[frequence]
  if (!unite) return null

  // Le champ est-il RÉELLEMENT une date ? Même prédicat que `getTransformedData`, qui décide de
  // poser un libellé de date : si lui ne l'a pas fait, la catégorie n'est pas une période et il n'y
  // a rien à borner. Garde aussi contre les tableaux de dates, sur lesquels le back refuse `>=`.
  const champGroupBy = groupByField?.uuid || groupByField?.id
  const schema = (fieldsData ?? []).find(f => f.uuid === champGroupBy)
  if (schema?.dataType !== "Date") return null

  const cleValeur = groupByField?.valueId || champGroupBy
  if (!cleValeur) return null

  // On retrouve la ligne en RECALCULANT le libellé avec la fonction qui a produit l'axe, plutôt
  // qu'en comparant à la valeur brute : c'est le libellé que le graphique nous renvoie.
  //
  // Plusieurs lignes peuvent partager un libellé (sans regroupement, deux horodatages d'un même
  // jour). Sans importance ici : un libellé qui porte l'année désigne un compartiment unique, donc
  // toutes ces lignes tombent dans le même intervalle et la première suffit.
  const ligne = (rows ?? []).find(
    r => String(getFinalDateLabel(r?.[cleValeur], groupByField?.frequency, trad ?? {}, languageId)) === String(categoryValue)
  )
  if (!ligne) return null

  const date = parseValeurBrute(ligne[cleValeur])
  if (!date.isValid()) return null

  const debut = date.clone().startOf(unite)
  const finExclue = debut.clone().add(1, unite)

  // ⚠️ Borne de fin EXCLUE (`<`), jamais un `<=` sur le dernier jour : la colonne est un
  // horodatage, et le back convertit « 2026-02-28 » en `TIMESTAMP '2026-02-28 00:00:00'`
  // (`parseDate`, `etl.utils.ts`). Un `<=` laisserait donc tomber toutes les fiches du dernier
  // jour ayant une heure — soit, sur un mois, la quasi-totalité d'une journée de production.
  return [
    { fieldUUID, operation: ">=", value: debut.format("YYYY-MM-DD") },
    { fieldUUID, operation: "<", value: finExclue.format("YYYY-MM-DD") },
  ]
}
